import { Component, lazy, Suspense, useState, useEffect, useLayoutEffect, useRef } from 'react';
import {
  BrowserRouter,
  Routes,
  Route,
  Outlet,
  Navigate,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';

import Navbar from './components/Navbar';
import ScrollToTop from './components/ScrollToTop';
import Loader from './components/Loader/Loader';
import MetaPixelConsent from './components/MetaPixelConsent';
import { AuthProvider } from './context/AuthContext';
import { LoginDrawerProvider, useLoginDrawer } from './context/LoginDrawerContext';
import { CartProvider } from './context/CartContext';
import { StoreCartProvider } from './context/StoreCartContext';
import StoreCartHost from './components/StoreCartHost/StoreCartHost';
import { ToastProvider } from './context/ToastContext';
import { LanguageProvider } from './context/LanguageContext';
import RoutePaintNotifier from './components/RoutePaintNotifier';
import LanguageUrlSync from './components/LanguageUrlSync';
import {
  captureUtmFromUrl,
  resolveVerifiedCampaignPoojaLandingPath,
} from './utils/utm';
import { FeatureFlagsProvider, useFeatureFlags } from './context/FeatureFlagsContext';
import { FEATURE_KEYS } from './utils/featureFlags';
import { captureCuberaParamsFromUrl } from './utils/cuberaPixel';
import { isInAppWebView } from './utils/inAppWebView';
import { holdSplashChrome, releaseSplashChrome } from './utils/splashChrome';
import {
  shouldSkipSplashThisSession,
  shouldSkipSplashForPath,
  shouldSkipMobileSplashForPerf,
  markSplashSeenThisSession,
  hideStaticHtmlSplash,
} from './utils/splashSession';
import { shouldHideMobileChromeInAppView, syncInAppViewSession, isPoojaBookPathname } from './utils/bookPageMobileChrome';
import { isPoojaDetailPathname } from './utils/poojaRoutes';
import FooterRouteFallback from './components/Footer/FooterRouteFallback';
import useDeferredFooterMount, { useDeferredMount } from './hooks/useDeferredFooterMount';
import useTrackVisit from './hooks/useTrackVisit';
import useGoogleAnalytics from './hooks/useGoogleAnalytics';
import usePlausible from './hooks/usePlausible';
import useOpenPanelPageLand from './hooks/useOpenPanelPageLand';
import { useAuth } from './context/AuthContext';
import { useLanguage } from './context/LanguageContext';
import { isChunkLoadFailure, reloadOnceForChunkFailure } from './utils/chunkRecovery';

const SplashScreen = lazy(() =>
  import(/* webpackChunkName: "splash-screen" */ './components/SplashScreen')
);
const Footer = lazy(() =>
  import(/* webpackChunkName: "footer" */ './components/Footer')
);
const GuruSlider = lazy(() =>
  import(/* webpackChunkName: "guru-slider" */ './components/GuruSlider/GuruSlider')
);
const LoginDrawer = lazy(() =>
  import(/* webpackChunkName: "login-drawer" */ './components/LoginDrawer')
);
const FooterFabStack = lazy(() =>
  import(/* webpackChunkName: "footer-fab" */ './components/Footer/FooterFabStack')
);

const Home = lazy(() => import(/* webpackChunkName: "home" */ './pages/Home'));
const About = lazy(() => import(/* webpackChunkName: "about" */ './pages/About'));
const Temple = lazy(() => import(/* webpackChunkName: "temple" */ './pages/Temple'));
const Astrology = lazy(() => import('./pages/Astrology'));
const NRI = lazy(() => import('./pages/NRI'));
const UpcomingPoojas = lazy(() => import('./pages/Poojas'));
const Contact = lazy(() => import('./pages/Contact'));
const MobileApp = lazy(() => import('./pages/MobileApp'));
const Gallery = lazy(() => import('./pages/Gallery/Gallery'));
const Store = lazy(() => import('./pages/Store'));
const StoreOrderThankYou = lazy(() => import('./pages/Store/StoreOrderThankYou'));
const DaivNitya = lazy(() => import('./pages/DaivNitya'));
const DaivNityaGrid = lazy(() => import('./pages/DaivNitya/DaivNityaGrid'));
const LivePooja = lazy(() => import(/* webpackChunkName: "live-pooja" */ './pages/LivePooja'));
const LiveTvDetail = lazy(() =>
  import(/* webpackChunkName: "live-tv-detail" */ './pages/LivePooja/LiveTvDetail')
);
const LiveChat = lazy(() => import(/* webpackChunkName: "live-chat" */ './pages/LiveChat'));
const PoojaDetail = lazy(() => import('./pages/PoojaDetail'));
const BookingForm = lazy(() => import('./pages/Booking'));
const Orders = lazy(() => import('./pages/Orders'));
const ProfilePage = lazy(() => import('./pages/Profile'));
const Cart = lazy(() => import('./pages/Cart'));
const Blogs = lazy(() => import('./pages/Blogs/Blogs'));
const BlogDetail = lazy(() => import('./pages/Blogs/BlogDetail'));
const InvoicePage = lazy(() => import('./pages/Invoice/InvoicePage'));
const RefundPolicy = lazy(() => import('./pages/Policies/RefundPolicy'));
const TermsAndConditions = lazy(() => import('./pages/Policies/Terms'));
const PrivacyPolicy = lazy(() => import('./pages/Policies/PrivacyPolicy'));
const MobileAppPrivacyPolicy = lazy(() => import('./pages/Policies/MobileAppPrivacyPolicy'));
const MobileAppTerms = lazy(() => import('./pages/Policies/MobileAppTerms'));
const CancellationRefundPolicy = lazy(() => import('./pages/Policies/CancellationRefund'));
const PayPage = lazy(() => import('./pages/Pay/PayPage'));
const FeedbackPage = lazy(() => import('./pages/Feedback/FeedbackPage'));

function LegacyBlogRedirect() {
  const { slug } = useParams();
  const { search } = useLocation();
  return <Navigate to={`/blogs/${slug}${search || ''}`} replace />;
}

function LoginRedirect() {
  const { open } = useLoginDrawer();
  useLayoutEffect(() => {
    open();
  }, [open]);
  return <Navigate to="/" replace />;
}

function AboutRouteFallback() {
  return (
    <div
      className="daiv-route-fallback daiv-route-fallback--about"
      role="status"
      aria-live="polite"
      aria-label="Loading About"
    >
      <div className="daiv-about-skeleton-hero" aria-hidden="true" />
      <div className="daiv-about-skeleton-row" aria-hidden="true">
        <div className="daiv-about-skeleton-block" />
        <div className="daiv-about-skeleton-block" />
      </div>
    </div>
  );
}

function RouteFallback() {
  const { pathname } = useLocation();
  if (pathname.startsWith('/about')) {
    return <AboutRouteFallback />;
  }
  // Book / thank-you / pay: avoid pale skeleton flash during payment → thank-you remount
  if (
    pathname.includes('/book') ||
    pathname.startsWith('/thank-you') ||
    pathname.startsWith('/pay') ||
    pathname.startsWith('/order-confirmed')
  ) {
    return null;
  }
  return (
    <div className="daiv-route-fallback daiv-route-fallback--skeleton" role="status" aria-live="polite">
      <div className="daiv-route-fallback-hero" aria-hidden="true" />
    </div>
  );
}

function SplashChunkFallback() {
  useLayoutEffect(() => {
    holdSplashChrome();
  }, []);
  return (
    <div
      className="splash-screen--chunk-loading"
      role="dialog"
      aria-label="Loading Daiv Bharathi"
      aria-busy="true"
    />
  );
}

function LoginDrawerHost() {
  const { isOpen } = useLoginDrawer();
  if (!isOpen) return null;
  return (
    <Suspense fallback={null}>
      <LoginDrawer />
    </Suspense>
  );
}

const isChunkLoadError = isChunkLoadFailure;

class ChunkLoadBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error) {
    if (isChunkLoadError(error)) {
      console.warn('[App] Lazy chunk failed to load; reloading once for the latest bundle.', error);
      reloadOnceForChunkFailure();
    }
  }

  render() {
    const { error } = this.state;
    if (error && isChunkLoadError(error)) {
      return (
        <div className="daiv-route-fallback" role="alert" aria-live="assertive">
          <p>Page assets are still loading or changed during development.</p>
          <button type="button" className="daiv-book-pooja-btn" onClick={() => window.location.reload()}>
            Reload page
          </button>
        </div>
      );
    }
    if (error) throw error;
    return this.props.children;
  }
}

function RouteChunkBoundary({ children }) {
  const location = useLocation();
  return <ChunkLoadBoundary key={location.key || location.pathname}>{children}</ChunkLoadBoundary>;
}

function UtmCapture() {
  const { search } = useLocation();
  useEffect(() => {
    captureUtmFromUrl(search);
    captureCuberaParamsFromUrl(search);
  }, [search]);
  return null;
}

/**
 * Home + utm_campaign: open pooja only if that campaign slug is a real pooja.
 * Website campaigns (unknown slug) stay on home; UTMs still captured by UtmCapture.
 */
function UtmCampaignLandingRedirect() {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const redirectedRef = useRef(false);
  useEffect(() => {
    if (redirectedRef.current) return;
    if (pathname !== '/' && pathname !== '') return;
    let cancelled = false;
    (async () => {
      const target = await resolveVerifiedCampaignPoojaLandingPath(search);
      if (cancelled || !target || redirectedRef.current) return;
      redirectedRef.current = true;
      navigate({ pathname: target, search }, { replace: true });
    })();
    return () => {
      cancelled = true;
    };
  }, [pathname, search, navigate]);
  return null;
}

function TrackVisits() {
  const { user } = useAuth();
  useTrackVisit(user?.id);
  return null;
}

function GoogleAnalytics() {
  useGoogleAnalytics();
  return null;
}

function PlausibleAnalytics() {
  usePlausible();
  return null;
}

/** Links OpenObserve RUM sessions to the signed-in devotee (id only). */
function OpenObserveUser() {
  const { user } = useAuth();
  const userId = user?.id ?? user?.userId ?? user?.user_id ?? null;
  useEffect(() => {
    import('./utils/openobserveInit').then((m) => m.setOpenObserveUser(userId));
  }, [userId]);
  return null;
}

function OpenPanelPageLand() {
  useOpenPanelPageLand();
  return null;
}

/** Linear-style enter: short fade + 6px settle. Chrome (nav) stays put. */
function RouteStage() {
  const { pathname } = useLocation();
  return (
    <div key={pathname} className="daiv-route-stage">
      <Outlet />
    </div>
  );
}

function Layout() {
  const { pathname, search } = useLocation();
  const mountFooterBody = useDeferredFooterMount(4000);
  const mountGuruSlider = useDeferredMount(5500);
  const isProfilePage = pathname === '/profile';
  /* Treat only the Reels-style routes as the immersive Daiv Nitya view —
     the grid/profile page (/daiv-nitya/grid) should keep the normal mobile chrome. */
  const isDaivNityaPage =
    pathname === '/daiv-nitya' ||
    (pathname.startsWith('/daiv-nitya/') && pathname !== '/daiv-nitya/grid');
  const hideInAppMobileChrome = shouldHideMobileChromeInAppView(pathname, search);
  const isBookingFlowPage = isPoojaBookPathname(pathname);
  const isPayPage = pathname === '/pay';
  const isPoojaDetailPage = isPoojaDetailPathname(pathname);
  const isLiveTvPlayerPage = pathname.startsWith('/live-tv/');
  const isMobileAppLegalPage =
    pathname.replace(/\/+$/, '') === '/mobile-app/privacy-policy' ||
    pathname.replace(/\/+$/, '') === '/mobile-app/terms-and-conditions';
  const hideSiteChrome =
    isDaivNityaPage ||
    hideInAppMobileChrome ||
    isBookingFlowPage ||
    isPayPage ||
    isLiveTvPlayerPage ||
    isMobileAppLegalPage;
  const hideGuruAndFooter = hideSiteChrome || isPoojaDetailPage;
  const useBookingChrome = isBookingFlowPage || isPayPage;

  useLayoutEffect(() => {
    syncInAppViewSession(search);
    document.documentElement.classList.toggle('daiv-in-app-booking-view', hideInAppMobileChrome);
  }, [search, hideInAppMobileChrome]);

  return (
    <div
      className={`daiv-app ${isProfilePage ? 'daiv-app--profile' : ''}${
        isDaivNityaPage ? ' daiv-app--daiv-nitya' : ''
      }${hideInAppMobileChrome ? ' daiv-app--book-immersive' : ''}${
        useBookingChrome ? ' daiv-app--booking-flow' : ''
      }${isPayPage ? ' daiv-app--pay-page' : ''}${isPoojaDetailPage ? ' daiv-app--pooja-detail' : ''}${
        isMobileAppLegalPage ? ' daiv-app--mobile-app-legal' : ''
      }`}
    >
      {!isMobileAppLegalPage && <Navbar />}
      {!isMobileAppLegalPage && <StoreCartHost />}
      {/* Reserve a viewport of height while the route chunk loads so the guru card / footer
          below are not painted in view and then pushed thousands of px down (CLS). */}
      <Suspense fallback={<div className="daiv-route-fallback" aria-hidden="true" />}>
        <RoutePaintNotifier />
        <RouteStage />
      </Suspense>
      {!hideSiteChrome && (
        <Suspense fallback={null}>
          <FooterFabStack />
        </Suspense>
      )}
      {!hideGuruAndFooter && (
        <div className="daiv-guru-slider-above-footer">
          {mountGuruSlider ? (
            <Suspense fallback={<div className="daiv-guru-slider-placeholder" aria-hidden="true" />}>
              <GuruSlider />
            </Suspense>
          ) : (
            <div className="daiv-guru-slider-placeholder" aria-hidden="true" />
          )}
        </div>
      )}
      {!hideGuruAndFooter &&
        (mountFooterBody ? (
          <Suspense fallback={<FooterRouteFallback />}>
            <Footer />
          </Suspense>
        ) : (
          <FooterRouteFallback />
        ))}
    </div>
  );
}

function LanguageSwitchOverlay() {
  const { isSwitchingLanguage } = useLanguage();
  if (!isSwitchingLanguage) return null;
  return (
    <div className="daiv-lang-switch-indicator" role="status" aria-live="polite" aria-label="Switching language">
      <Loader size="small" />
    </div>
  );
}

function FlaggedRoute({ featureKey, children }) {
  const { ready, isEnabled } = useFeatureFlags();
  if (!ready) return null;
  if (!isEnabled(featureKey)) return <Navigate to="/" replace />;
  return children;
}

function App() {
  const [showSplash, setShowSplash] = useState(() => {
    if (process.env.REACT_APP_ENABLE_SPLASH === 'false') return false;
    if (typeof window === 'undefined') return true;
    if (shouldSkipSplashThisSession()) return false;
    if (shouldSkipSplashForPath(window.location.pathname)) return false;
    if (shouldSkipMobileSplashForPerf()) return false;

    const isMobileViewport = window.matchMedia('(max-width: 768px)').matches;
    const allowMobileSplash = process.env.REACT_APP_ENABLE_SPLASH_MOBILE === 'true';
    const allowDesktopSplash = process.env.REACT_APP_ENABLE_SPLASH_DESKTOP !== 'false';

    if (isMobileViewport) return allowMobileSplash;
    return allowDesktopSplash;
  });

  useLayoutEffect(() => {
    if (!showSplash) {
      releaseSplashChrome();
      hideStaticHtmlSplash();
    }
  }, [showSplash]);

  const [routePainted, setRoutePainted] = useState(false);

  useEffect(() => {
    const onPainted = () => setRoutePainted(true);
    window.addEventListener('daiv-route-painted', onPainted);
    return () => window.removeEventListener('daiv-route-painted', onPainted);
  }, []);

  const handleSplashFadeStart = () => {
    releaseSplashChrome();
  };

  const handleSplashComplete = () => {
    markSplashSeenThisSession();
    releaseSplashChrome();
    hideStaticHtmlSplash();
    setShowSplash(false);
  };

  useLayoutEffect(() => {
    document.documentElement.classList.toggle('daiv-in-app-webview', isInAppWebView());
  }, []);

  const useLightweightSplash =
    typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches;

  return (
    <HelmetProvider>
    <LanguageProvider>
      <FeatureFlagsProvider>
      <AuthProvider>
        <LoginDrawerProvider>
        <CartProvider>
        <StoreCartProvider>
        <BrowserRouter>
        <ToastProvider>
          {/* Scroll target for “back to top” — always in DOM (works when #home header is display:none on mobile) */}
          <div id="daiv-top-anchor" className="daiv-top-anchor" aria-hidden="true" tabIndex={-1} />
          <div className="daiv-app-shell">
          {!showSplash && <MetaPixelConsent />}
          <LanguageSwitchOverlay />
          <ScrollToTop />
          <UtmCapture />
          <UtmCampaignLandingRedirect />
          <TrackVisits />
          <GoogleAnalytics />
          <PlausibleAnalytics />
          <OpenPanelPageLand />
          <OpenObserveUser />
          <LanguageUrlSync />
          <LoginDrawerHost />
          {showSplash && (
            <Suspense fallback={<SplashChunkFallback />}>
              <SplashScreen
                onFadeStart={handleSplashFadeStart}
                onComplete={handleSplashComplete}
                lightweight={useLightweightSplash}
                routeReady={routePainted}
              />
            </Suspense>
          )}
          <RouteChunkBoundary>
            <Suspense fallback={<RouteFallback />}>
              <Routes>
                <Route path="/invoice/:bookingId" element={<InvoicePage />} />
                <Route path="/feedback/:bookingId" element={<FeedbackPage />} />
                <Route path="/live-chat/:chatRoomId" element={<LiveChat />} />
                <Route path="/" element={<Layout />}>
                  <Route index element={<Home />} />
                  <Route path="about" element={<About />} />
                  <Route path="temple" element={<Temple />} />
                  <Route path="astrology" element={<Astrology />} />
                  <Route path="nri" element={<NRI />} />
                  <Route path="poojas" element={<UpcomingPoojas />} />
                  <Route path="pooja" element={<Navigate to="/poojas" replace />} />
                  <Route path="pooja/" element={<Navigate to="/poojas" replace />} />
                  <Route
                    path="store"
                    element={
                      <FlaggedRoute featureKey={FEATURE_KEYS.WEB_STORE}>
                        <Store />
                      </FlaggedRoute>
                    }
                  />
                  <Route
                    path="store/thank-you/:orderId"
                    element={
                      <FlaggedRoute featureKey={FEATURE_KEYS.WEB_STORE}>
                        <StoreOrderThankYou />
                      </FlaggedRoute>
                    }
                  />
                  <Route path="blogs" element={<Blogs />} />
                  <Route path="blogs/:slug" element={<BlogDetail />} />
                  <Route path="blog/:slug" element={<LegacyBlogRedirect />} />
                  <Route path="upcoming" element={<Navigate to="/poojas" replace />} />
                  <Route path="orders" element={<Orders />} />
                  <Route path="profile" element={<ProfilePage />} />
                  <Route path="cart" element={<Cart />} />
                  <Route path="past" element={<UpcomingPoojas />} />
                  <Route path="contact" element={<Contact />} />
                  <Route path="mobile-app/privacy-policy" element={<MobileAppPrivacyPolicy />} />
                  <Route path="mobile-app/terms-and-conditions" element={<MobileAppTerms />} />
                  <Route path="mobile-app" element={<MobileApp />} />
                  <Route path="gallery" element={<Gallery />} />
                  <Route
                    path="live-tv"
                    element={
                      <FlaggedRoute featureKey={FEATURE_KEYS.LIVE_TV}>
                        <LivePooja />
                      </FlaggedRoute>
                    }
                  />
                  <Route
                    path="live-tv/:poojaId"
                    element={
                      <FlaggedRoute featureKey={FEATURE_KEYS.LIVE_TV}>
                        <LiveTvDetail />
                      </FlaggedRoute>
                    }
                  />
                  <Route path="daiv-nitya" element={<DaivNitya />} />
                  <Route path="daiv-nitya/grid" element={<DaivNityaGrid />} />
                  <Route path="daiv-nitya/:id" element={<DaivNitya />} />
                  <Route path="pay" element={<PayPage />} />
                  <Route path="login" element={<LoginRedirect />} />
                  <Route path="pooja/live/:slug/book" element={<BookingForm />}>
                    <Route path="pay" element={null} />
                  </Route>
                  <Route path="pooja/upcoming/:slug/book" element={<BookingForm />}>
                    <Route path="pay" element={null} />
                  </Route>
                  <Route path="pooja/past/:slug/book" element={<BookingForm />}>
                    <Route path="pay" element={null} />
                  </Route>
                  <Route path="pooja/live/:slug" element={<PoojaDetail />} />
                  <Route path="pooja/upcoming/:slug" element={<PoojaDetail />} />
                  <Route path="pooja/past/:slug" element={<PoojaDetail />} />
                  <Route path="pooja/:slug" element={<PoojaDetail />} />
                  <Route path="pooja/:slug/book" element={<BookingForm />}>
                    <Route path="pay" element={null} />
                  </Route>
                  <Route path="thank-you/:slug/:bookingId" element={<BookingForm />} />
                  <Route path="thank-you/:slug" element={<BookingForm />} />
                  <Route path="order-confirmed/:bookingId" element={<BookingForm />} />
                  <Route path="refund-policy" element={<RefundPolicy />} />
                  <Route path="terms-and-conditions" element={<TermsAndConditions />} />
                  <Route path="privacy-policy" element={<PrivacyPolicy />} />
                  <Route path="cancellation-refund-policy" element={<CancellationRefundPolicy />} />
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Route>
              </Routes>
            </Suspense>
          </RouteChunkBoundary>
          </div>
        </ToastProvider>
        </BrowserRouter>
        </StoreCartProvider>
        </CartProvider>
        </LoginDrawerProvider>
      </AuthProvider>
      </FeatureFlagsProvider>
    </LanguageProvider>
    </HelmetProvider>
  );
}

export default App;
